/**
 * Dermowise Notification Manager
 * Handles push notification registration and management
 */

const NOTIFICATION_WORKER_URL = 'https://dermowise-notifications.er-hooman.workers.dev';

class NotificationManager {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.registration = null;
    this.subscription = null;
  }

  /**
   * Initialize notification system
   */
  async initialize() {
    if (!this.isSupported) {
      console.warn('🔔 Push notifications are not supported in this browser');
      return false;
    }

    try {
      console.log('🔔 Initializing notification system...');
      
      // Use existing PWA service worker
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });
      console.log('✅ Service Worker registered:', this.registration);
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      // Check if already subscribed
      this.subscription = await this.registration.pushManager.getSubscription();
      
      if (this.subscription) {
        console.log('✅ Already subscribed to push notifications');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Failed to initialize notifications:', error);
      return false;
    }
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported');
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('🔔 Notification permission:', permission);
      
      if (permission === 'granted') {
        return true;
      } else if (permission === 'denied') {
        throw new Error('Notification permission denied');
      } else {
        throw new Error('Notification permission not granted');
      }
    } catch (error) {
      console.error('❌ Permission request failed:', error);
      throw error;
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe() {
    if (!this.registration) {
      throw new Error('Service Worker not registered');
    }

    try {
      console.log('🔔 Subscribing to push notifications...');
      
      // Create VAPID key (you'll need to get this from your notification worker)
      const vapidPublicKey = 'BG4pdgHGbnc5Yw-L2z9Td0qRNpWdaOH9zq6AIg3Exx4MCskGB-J0_O1kW1xR-PdhWFZ5ycGSuUkM5Grl2YjfSvU'; // Your actual VAPID public key
      
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });
      
      console.log('✅ Push subscription created:', this.subscription);
      
      // Send subscription to notification worker
      await this.sendSubscriptionToWorker(this.subscription);
      
      return this.subscription;
    } catch (error) {
      console.error('❌ Subscription failed:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    if (!this.subscription) {
      console.log('🔔 No active subscription to unsubscribe');
      return true;
    }

    try {
      console.log('🔔 Unsubscribing from push notifications...');
      
      const success = await this.subscription.unsubscribe();
      
      if (success) {
        this.subscription = null;
        console.log('✅ Successfully unsubscribed');
      }
      
      return success;
    } catch (error) {
      console.error('❌ Unsubscribe failed:', error);
      throw error;
    }
  }

  /**
   * Send subscription to notification worker
   */
  async sendSubscriptionToWorker(subscription, userId = null) {
    try {
      // Get userId from Firebase if not provided
      if (!userId) {
        const user = await this.getCurrentUser();
        userId = user?.uid || 'anonymous';
      }

      const response = await fetch(`${NOTIFICATION_WORKER_URL}/api/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subscription: subscription,
          userId: userId,
          timestamp: Date.now()
        })
      });

      if (response.ok) {
        console.log('✅ Subscription sent to notification worker');
        return true;
      } else {
        throw new Error(`Failed to send subscription: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Failed to send subscription to worker:', error);
      throw error;
    }
  }

  /**
   * Get current Firebase user
   */
  async getCurrentUser() {
    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      return auth.currentUser;
    } catch (error) {
      console.error('❌ Failed to get current user:', error);
      return null;
    }
  }

  /**
   * Send test notification
   */
  async sendTestNotification() {
    try {
      console.log('🔔 Sending test notification...');
      
      const response = await fetch(`${NOTIFICATION_WORKER_URL}/api/notifications/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Test notification sent:', result);
        return result;
      } else {
        throw new Error(`Failed to send test notification: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Failed to send test notification:', error);
      throw error;
    }
  }

  /**
   * Check notification status
   */
  async getStatus() {
    try {
      const response = await fetch(`${NOTIFICATION_WORKER_URL}/api/notifications/status`);
      
      if (response.ok) {
        const status = await response.json();
        console.log('📊 Notification service status:', status);
        return status;
      } else {
        throw new Error(`Failed to get status: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Failed to get notification status:', error);
      throw error;
    }
  }

  /**
   * Convert VAPID key to Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Get current subscription
   */
  getSubscription() {
    return this.subscription;
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled() {
    return this.subscription !== null;
  }

  /**
   * Get permission status
   */
  getPermissionStatus() {
    if (!this.isSupported) {
      return 'unsupported';
    }
    return Notification.permission;
  }
}

// Create singleton instance
const notificationManager = new NotificationManager();

export default notificationManager;
